/**
 * Resolve artist IDs → shot targets via globe points (country + lat/lng).
 * Skips are always listed — never silently dropped.
 */

import type { GlobePoint } from "#shared/schemas/globe.ts";

export type ShotTarget = {
  id: string;
  artistId: string;
  artistName: string;
  countryCode: string;
  countryLabel: string;
  lat: number;
  lng: number;
};

export type ShotSkip = {
  artistId: string;
  artistName: string;
  reason: string;
};

export type ResolveShotResult = {
  targets: ShotTarget[];
  skipped: ShotSkip[];
};

function artistIndex(points: GlobePoint[]): Map<string, GlobePoint> {
  const m = new Map<string, GlobePoint>();
  for (const p of points) {
    for (const id of p.artistIds) {
      if (!m.has(id)) m.set(id, p);
    }
  }
  return m;
}

export function resolveArtistIdsToTargets(
  artistIds: string[],
  artistNames: Map<string, string>,
  points: GlobePoint[],
): ResolveShotResult {
  const byArtist = artistIndex(points);
  const targets: ShotTarget[] = [];
  const skipped: ShotSkip[] = [];
  const seenArtist = new Set<string>();

  for (const artistId of artistIds) {
    if (seenArtist.has(artistId)) continue;
    seenArtist.add(artistId);
    const name = artistNames.get(artistId) ?? artistId;
    const point = byArtist.get(artistId);
    if (!point || !point.countryCode) {
      skipped.push({
        artistId,
        artistName: name,
        reason: "No country data",
      });
      continue;
    }
    targets.push({
      id: `shot_${artistId}`,
      artistId,
      artistName: name,
      countryCode: point.countryCode,
      countryLabel: point.label,
      lat: point.lat,
      lng: point.lng,
    });
  }

  return { targets, skipped };
}

/** Keep first target per country; report the rest as skipped duplicates. */
export function dedupeTargetsByCountry(targets: ShotTarget[]): {
  kept: ShotTarget[];
  skipped: ShotSkip[];
} {
  const seen = new Set<string>();
  const kept: ShotTarget[] = [];
  const skipped: ShotSkip[] = [];
  for (const t of targets) {
    if (seen.has(t.countryCode)) {
      skipped.push({
        artistId: t.artistId,
        artistName: t.artistName,
        reason: `Duplicate country (${t.countryLabel})`,
      });
      continue;
    }
    seen.add(t.countryCode);
    kept.push(t);
  }
  return { kept, skipped };
}

export function slugifyFilenamePart(raw: string): string {
  return raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 60) || "unknown";
}

export function shotFilename(
  target: ShotTarget,
  presetName: string,
  mimeType = "video/webm",
): string {
  const artist = slugifyFilenamePart(target.artistName);
  const country = slugifyFilenamePart(target.countryLabel || target.countryCode);
  const preset = slugifyFilenamePart(presetName);
  const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";
  return `${artist}--${country}--${preset}.${ext}`;
}
